import {cruisesStore, persistentStore} from '@/composables/store-accessor'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    RESET as RESET_EVENT,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '@/utils/cruises/cruises-blank-states'
import {appInstance} from '@/utils/app-accessor'
import {PRODUCT_NAME} from '@/utils/cruises/cruises-const'
import {newSearch as newSearchFn, search as searchFn} from '@/utils/store-helpers'
import {onmessage} from '@/utils/worker-helpers'
import {loadCity} from '@/stores/tours/ToursRuntimeStore'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'
import type {Ref} from 'vue'
import {clean} from '~/utils/helpers'

export const useCruisesRuntimeStore = defineStore('cruisesRuntime', () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        SET_OFFERS,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
        stopSearch,
        searchActiveCount,
        START_SEARCH,
        STOP_SEARCH,
        STOP_FILTER,
        filterActive,
    } = productRuntimeStore

    const departureCity: Ref = ref({})
    const departureCountry: Ref = ref({})
    const destinationCity: Ref = ref({})
    const destinationCountry: Ref = ref({})

    function RESET() {
        searchActiveCount.value = 0
    }

    function SET_DEPARTURE_CITY(val: any) {
        departureCity.value = val
        departureCountry.value = {}
    }

    function SET_DEPARTURE_COUNTRY(val: any) {
        departureCountry.value = val
        departureCity.value = {}
    }

    function SET_DESTINATION_CITY(val: any) {
        destinationCity.value = val
        destinationCountry.value = {}
    }

    function SET_DESTINATION_COUNTRY(val: any) {
        destinationCountry.value = val
        destinationCity.value = {}
    }

    async function loadDepartureCity(id: any) {
        if (!id) {
            return
        }

        departureCity.value = ((await loadCity('departureCity', id)) as any).departureCity
        departureCountry.value = {}
    }

    async function loadDepartureCountry(id: any) {
        try {
            const {
                countries: [_country],
            } = await appInstance.$api.locations.get({id, limitCountries: 1})
            departureCountry.value = _country
            departureCity.value = {}
        } catch (e) {
            departureCountry.value = {}
        }
    }

    async function loadDestinationCity(id: any) {
        destinationCity.value = ((await loadCity('destinationCity', id)) as any).destinationCity
        destinationCountry.value = {}
    }

    async function loadDestinationCountry(id: any) {
        try {
            const {
                countries: [_country],
            } = await appInstance.$api.locations.get({id, limitCountries: 1})
            destinationCountry.value = _country
            destinationCity.value = {}
        } catch (e) {
            destinationCountry.value = {}
        }
    }

    function clientInit() {
        EventBus.$on(RESET_EVENT, reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        onmessage(PRODUCT_NAME, STOP_FILTER, filterActive, SET_OFFERS)
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        cruisesStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    async function reload() {}

    function reset() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), cruisesStore)
        RESET()
        cruisesStore.RESET()
    }

    function newSearch() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), cruisesStore)
    }

    async function search(rq: any) {
        START_SEARCH()
        newSearchFn(stopSearch, SET_OFFERS, rq, searchResponseBlank(), cruisesStore)
        EventBus.$emit(SEARCH_EVENT)
        await searchFn(
            SET_PARTIAL_SEARCH_RESULTS_CTS,
            rq,
            PRODUCT_NAME,
            cruisesStore,
            appInstance.$api.searchCruises,
            'products',
            ['duration', 'price']
        )
        STOP_SEARCH()
    }

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {partialResponse, convertToCurrency, ...query} = searchRequest
            return {name: 'cruises', query}
        }
    })

    const infoPageLink = computed(() => {
        return (product: any, searchRequest: any) => {
            const {query} = searchPageLink.value(searchRequest)
            query.name = product.name
            return {name: 'cruise', query}
        }
    })

    const searchRequestFromQuery = computed(() => {
        return (query: any) => {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequestBlank(), query)
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            } else {
                rq.childrenAges = rq.childrenAges.map((age: any) => parseInt(age, 10))
            }
            rq.convertToCurrency = persistentStore.currency
            rq.adults = parseInt(rq.adults, 10)
            if (rq.citizenshipId) {
                rq.citizenshipId = parseInt(rq.citizenshipId, 10)
            }
            clean(rq)

            return rq
        }
    })

    return {
        ...productRuntimeStore,
        departureCity,
        departureCountry,
        destinationCity,
        destinationCountry,
        RESET,
        searchActiveCount,
        SET_DEPARTURE_CITY,
        SET_DEPARTURE_COUNTRY,
        SET_DESTINATION_CITY,
        SET_DESTINATION_COUNTRY,
        loadDepartureCity,
        loadDepartureCountry,
        loadDestinationCity,
        loadDestinationCountry,
        clientInit,
        changePrice,
        reload,
        reset,
        newSearch,
        search,
        searchPageLink,
        infoPageLink,
        searchRequestFromQuery,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useCruisesRuntimeStore, import.meta.hot))
